import { useState } from 'react'
import { ExternalLink, FolderPlus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { createEngagementDriveFolderFn } from '@/lib/firebase/functions'
import { formatUserFacingError } from '@/lib/errors'

interface Props {
  engagementId: string | null
  driveFolderUrl?: string
  driveFolderName?: string
  legacyDriveLink?: string  // pre-Drive-integration free-text URL, read-only
  onCreated?: () => void
}

export function EngagementDriveFolder({
  engagementId,
  driveFolderUrl,
  driveFolderName,
  legacyDriveLink,
  onCreated,
}: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Optimistic local copy so the cell flips to the linked state immediately,
  // without waiting for the parent to refetch the engagement.
  const [local, setLocal] = useState<{ url: string; name: string } | null>(null)

  const effectiveUrl = driveFolderUrl ?? local?.url
  const effectiveName = driveFolderName ?? local?.name

  async function handleCreate() {
    if (!engagementId) return
    setError(null)
    setBusy(true)
    try {
      const { data } = await createEngagementDriveFolderFn({ engagementId })
      setLocal({ url: data.folderUrl, name: data.folderName })
      onCreated?.()
    } catch (err) {
      setError(formatUserFacingError(
        err,
        'We could not create the Drive folder. Please try again.',
        'drive/create-folder',
      ))
    } finally {
      setBusy(false)
    }
  }

  if (!engagementId) {
    return (
      <p className="text-xs text-muted-foreground">
        Save the engagement first, then a Drive folder can be created.
      </p>
    )
  }

  if (effectiveUrl) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
        <a
          href={effectiveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline truncate"
          title={effectiveName}
        >
          {effectiveName ?? 'Open folder'}
        </a>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handleCreate} disabled={busy}>
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <FolderPlus className="h-3 w-3 mr-1" />
          )}
          Create folder now
        </Button>
        <span className="text-xs text-muted-foreground">
          Auto-created on quote acceptance or deposit payment.
        </span>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {legacyDriveLink && (
        <p className="text-xs text-muted-foreground">
          Legacy link:{' '}
          <a
            href={legacyDriveLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
          >
            {legacyDriveLink}
          </a>
        </p>
      )}
    </div>
  )
}
